/**
 * Serviço de Comentários por Questão - Integração com Supabase
 *
 * Comentários sociais/compartilhados nas telas de resolver questão. Espelha o
 * fluxo de comentários da Comunidade (communityService), mas chaveado por
 * `questionId` (id local estável da questão, ex.: 'fcc_bb2011_001') na tabela
 * `question_comments`. Mesma árvore de respostas via parent_id e mesmo filtro
 * de autores bloqueados (Trust & Safety).
 */

import { supabase } from '../lib/supabase';
import { getCachedUser } from './userCacheService';
import { getBlockedUserIds } from './moderationService';
import { captureException } from './telemetry';

// Duplicado (mínimo) de communityService para manter os domínios desacoplados.
const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'agora';
    if (diffMins < 60) return `há ${diffMins} min`;
    if (diffHours < 24) return `há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
    if (diffDays < 30) return `há ${Math.floor(diffDays / 7)} semana${diffDays >= 14 ? 's' : ''}`;
    return `há ${Math.floor(diffDays / 30)} mês${diffDays >= 60 ? 'es' : ''}`;
};

// Constrói a hierarquia de respostas a partir da lista plana. Órfãos (pai
// bloqueado/removido) são reparentados como raiz, então respostas de não-
// bloqueados não somem.
const buildCommentTree = (comments) => {
    const commentMap = new Map();
    const rootComments = [];

    comments.forEach(comment => {
        commentMap.set(comment.id, { ...comment, replies: [] });
    });

    comments.forEach(comment => {
        const commentNode = commentMap.get(comment.id);
        if (comment.parentId) {
            const parent = commentMap.get(comment.parentId);
            if (parent) {
                parent.replies.push(commentNode);
            } else {
                rootComments.push(commentNode);
            }
        } else {
            rootComments.push(commentNode);
        }
    });

    return rootComments;
};

/**
 * Buscar comentários de uma questão (com hierarquia de threading).
 */
export const getQuestionComments = async (questionId) => {
    if (!questionId) return [];
    try {
        const { data, error } = await supabase
            .from('question_comments')
            .select('id, parent_id, author_name, author_avatar, author_avatar_url, content, votes, created_at, user_id')
            .eq('question_id', questionId)
            .order('created_at', { ascending: true })
            .limit(200);

        if (error) throw error;

        // Oculta comentários de autores bloqueados (Trust & Safety).
        const blockedIds = await getBlockedUserIds();
        const visibleComments = blockedIds.size > 0
            ? data.filter(comment => !blockedIds.has(comment.user_id))
            : data;

        const mappedComments = visibleComments.map(comment => ({
            id: comment.id,
            parentId: comment.parent_id,
            author: comment.author_name,
            avatar: comment.author_avatar || '👤',
            avatarUrl: comment.author_avatar_url,
            content: comment.content,
            votes: comment.votes || 0,
            time: formatTimeAgo(comment.created_at),
            date: new Date(comment.created_at).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }),
            userId: comment.user_id,
        }));

        return buildCommentTree(mappedComments);
    } catch (error) {
        captureException(error, { tags: { area: 'getQuestionComments' } });
        return [];
    }
};

/**
 * Adicionar um comentário a uma questão (com suporte a threading).
 */
export const addQuestionComment = async (questionId, content, parentId = null) => {
    try {
        if (!questionId) {
            return { success: false, error: 'Questão inválida.' };
        }

        const user = await getCachedUser();
        if (!user) {
            return { success: false, error: 'Você precisa estar logado para comentar.' };
        }

        const trimmed = String(content ?? '').trim();
        if (!trimmed) {
            return { success: false, error: 'Escreva um comentário.' };
        }

        const { data: profile } = await supabase
            .from('profiles')
            .select('nome, foto_url')
            .eq('id', user.id)
            .single();

        const { data, error } = await supabase
            .from('question_comments')
            .insert({
                question_id: questionId,
                user_id: user.id,
                parent_id: parentId,
                author_name: profile?.nome || user.email?.split('@')[0] || 'Anônimo',
                author_avatar: '👤',
                author_avatar_url: profile?.foto_url || null,
                content: trimmed,
            })
            .select()
            .single();

        if (error) throw error;

        return {
            success: true,
            data: {
                id: data.id,
                parentId: data.parent_id,
                author: data.author_name,
                avatar: data.author_avatar || '👤',
                avatarUrl: data.author_avatar_url,
                content: data.content,
                votes: 0,
                time: 'agora',
                date: new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }),
                userId: data.user_id,
                replies: [],
            },
        };
    } catch (error) {
        captureException(error, { tags: { area: 'addQuestionComment' } });
        return { success: false, error: error.message };
    }
};

export default {
    getQuestionComments,
    addQuestionComment,
};
